import { Box, Container, Flex, Progress } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigation } from 'react-router-dom';
import { DocsFooter } from './docs-footer';
import { OrganizationSwitcher } from './organization-switcher';
import Sidebar, { MobileNav } from './sidebar';
import { TawkTo } from './tawkto';

function TopLoader() {
    const navigation = useNavigation();
    const isLoading = navigation.state === "loading";

    const [visible, setVisible] = useState(false);
    const [value, setValue] = useState(null);

    const showTimer = useRef(null);
    const startTime = useRef(null);

    useEffect(() => {
        if (isLoading) {
            showTimer.current = setTimeout(() => {
                startTime.current = Date.now();
                setVisible(true);
            }, 150); // don't show for fast routes
            setValue(null);
        } else {
            clearTimeout(showTimer.current);
            setValue(100);

            if (startTime.current) {
                const elapsed = Date.now() - startTime.current;
                const remaining = Math.max(200 - elapsed, 0);

                setTimeout(() => {
                    setVisible(false);
                    startTime.current = null;
                }, remaining);
            }
        }

        return () => clearTimeout(showTimer.current);
    }, [isLoading]);

    if (!visible) {
        return null;
    }

    return (
        <Progress.Root
            size="xs"
            position="fixed"
            top="0"
            left="0"
            width="100%"
            zIndex="overlay"
            colorPalette="primary"
            value={value}
        >
            <Progress.Track>
                <Progress.Range animation="topLoader" position="relative" />
            </Progress.Track>
        </Progress.Root >
    );
}

export default function Layout({ children, organization, user, csrfToken, channels, campaigns, companyPages, orgID, importSharesMaxImportCount, importSharesMaxAgeDays, organizations }) {
    const isSignedInLayout = user && user.ID
    const isSuperAdmin = user?.UserType?.Name === "super_admin"

    return (
        <>
            <TopLoader />
            <Box
                background="bg.subtle"
                py={isSignedInLayout ? { base: 0, md: "layoutVerticalMargin" } : 0}
                px={isSignedInLayout ? { base: 0, md: "layoutHorizontalMargin" } : 0}
                minH="100vh"
            >
                {isSuperAdmin && organizations?.length > 0 && (
                    <OrganizationSwitcher
                        organizations={organizations}
                        organization={organization}
                        mb={{ base: 0, md: 4 }}
                    />
                )}
                {user && user.ID ? (
                    // the page is rendered once, with the top bar stacked above it on mobile and the sidebar beside it on desktop.
                    // Rendering it for each layout mounted every page twice, and a dialog a page opens on its own showed twice.
                    <Flex
                        direction={{ base: "column", md: "row" }}
                        gap={{ base: 0, md: "layoutGap" }}
                        align={{ base: "stretch", md: "start" }}
                        minH={{ base: "100dvh", md: "auto" }}
                        pb={{ base: "16", md: 0 }}
                    >
                        <Box hideFrom="md">
                            <MobileNav orgID={orgID} user={user} organization={organization} />
                        </Box>
                        <Sidebar
                            hideBelow="md"
                            p="3"
                            width="15rem"
                            flexShrink={0}
                            boxShadow="xs"
                            borderRadius="md"
                            orgID={orgID}
                            user={user}
                            organization={organization}
                            h="calc(100vh - 2*var(--chakra-spacing-layout-horizontal-margin))"
                            position="sticky"
                            alignSelf="flex-start"
                            top="layoutHorizontalMargin"
                        />
                        <Flex
                            direction="column"
                            flexGrow={1}
                            minW="0"
                            minH={{ md: "calc(100vh - 2*var(--chakra-spacing-layout-vertical-margin))" }}
                            pt={{ base: 0, md: "workspaceGap" }}
                        >
                            <Box>
                                {children ?? <Outlet context={{ orgID, organization, user, csrfToken, channels, campaigns, companyPages, importSharesMaxImportCount, importSharesMaxAgeDays }} />}
                            </Box>
                            <Container maxW="7xl" px={{ base: "4", md: "0" }} mt="auto" pt="workspaceGap">
                                <DocsFooter />
                            </Container>
                        </Flex>
                    </Flex>
                ) : (
                    <Box>
                        {children ?? <Outlet context={{ organization, user, csrfToken, channels, campaigns, companyPages, importSharesMaxImportCount, importSharesMaxAgeDays }} />}
                    </Box>
                )}
                <TawkTo user={user} />
            </Box>
        </>
    );
}
